import { useEffect, useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { formatDistanceToNow } from 'date-fns';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

interface Message {
  id: string;
  from_number: string;
  to_number: string;
  body: string;
  direction: 'inbound' | 'outbound';
  created_at: string;
}

interface MessageHistoryProps {
  businessId: string;
}

export function MessageHistory({ businessId }: MessageHistoryProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = useSupabase();

  useEffect(() => {
    const loadMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('sms_messages')
          .select('*')
          .eq('business_id', businessId)
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;
        setMessages(data || []);
      } catch (err) {
        console.error('Error loading messages:', err);
        setError('Failed to load message history');
      } finally {
        setIsLoading(false);
      }
    };

    loadMessages();

    // Set up real-time subscription
    const channel = supabase
      .channel('sms_messages_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'sms_messages',
          filter: `business_id=eq.${businessId}`,
        },
        (payload: RealtimePostgresChangesPayload<Message>) => {
          if (payload.new) {
            setMessages(current => [payload.new as Message, ...current]);
          }
        }
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, [businessId, supabase]);

  if (isLoading) {
    return <div className="text-gray-500">Loading message history...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  if (messages.length === 0) {
    return <div className="text-gray-500">No messages yet</div>;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">Message History</h3>
      <div className="space-y-2">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`p-4 rounded-lg ${
              message.direction === 'inbound'
                ? 'bg-blue-50 ml-4'
                : 'bg-gray-50 mr-4'
            }`}
          >
            <div className="flex justify-between items-start">
              <div className="text-sm text-gray-600">
                {message.direction === 'inbound' ? 'From: ' : 'To: '}
                {message.direction === 'inbound' ? message.from_number : message.to_number}
              </div>
              <div className="text-xs text-gray-500">
                {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
              </div>
            </div>
            <div className="mt-1 text-gray-900">{message.body}</div>
          </div>
        ))}
      </div>
    </div>
  );
} 